import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";

import { trackEvent } from "@utils/analytics";
import { ownsRole } from "@containers/appShell/navItems";

/**
 * Guard di RUOLO a livello di rotta (ProtectedRoute guarda solo l'auth).
 *
 * Fino al 18/08 /home/dashboard-genitori e /home/dashboard-professori erano
 * montate senza controlli: l'unica difesa era un navigate('/home') dentro la
 * pagina, che scattava DOPO il download del chunk e DOPO la query Firestore.
 * Chi non aveva il ruolo vedeva comparire e sparire una dashboard non sua.
 * Qui la decisione si prende prima di montare la pagina — con React.lazy nel
 * router il chunk non viene nemmeno scaricato.
 *
 * NON sostituisce il controllo dentro le pagine: quello legge Firestore ed e'
 * la difesa che vale (qui si legge il profilo in Redux, comodita' di UI).
 *
 * Fino al 19/08 questa frase era FALSA, ed e' il tipo di commento peggiore:
 * descriveva l'intenzione, non il codice. Le due pagine tenevano guard e
 * caricamento dati nello stesso try/catch/finally, quindi un errore di rete
 * sulla getDoc del proprio documento faceva comparire la dashboard senza che
 * `role` fosse mai stato valutato — il guard cadeva APERTO. Ora le due pagine
 * concedono solo su una risposta positiva e rimbalzano su tutto il resto,
 * errori compresi (STU-172). La regola per esteso, con il motivo per cui un
 * test sul caso felice non basta a provarla:
 * `docs/security/CORRETTEZZA-AREA-DOCENTI-2026-08-19.md`.
 *
 * Idratazione: finche' il profilo utente non e' in Redux non si rimbalza
 * nessuno, si aspetta. Rimbalzare un avente diritto solo perche' il suo
 * documento non e' ancora arrivato sarebbe il bug al contrario. Se l'utente
 * non arriva mai perche' non e' loggato, e' ProtectedRoute (rotta padre
 * `home`) a mandarlo alla landing.
 */
const RoleRoute = ({ role, element, redirectTo = "/home", fallback = null }) => {
  const userFromAuth = useSelector(state => state.auth?.user);
  const userFromUser = useSelector(state => state.user?.user);
  // stessa preferenza di navConfig.js/menu.js: il profilo completo se c'e'
  const user = userFromUser || userFromAuth;
  const { pathname } = useLocation();

  const isHydrated = !!(user && (user.uid || user.email));
  const isBlocked = isHydrated && !ownsRole(user, role);

  useEffect(() => {
    if (!isBlocked) return;
    // stessa famiglia degli eventi role_* dello switch di vista: quanti
    // tentativi di entrare in una vista non posseduta arrivano da link diretti
    trackEvent("role_route_blocked", {
      category: "navigation",
      role,
      path: pathname,
    });
  }, [isBlocked, role, pathname]);

  if (!isHydrated) return fallback;
  if (isBlocked) return <Navigate to={redirectTo} replace={true} />;

  return element;
};

RoleRoute.propTypes = {
  role: PropTypes.oneOf(["student", "parent", "teacher"]).isRequired,
  element: PropTypes.element.isRequired,
  redirectTo: PropTypes.string,
  fallback: PropTypes.node,
};

export default RoleRoute;
